import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Bar, Doughnut } from 'react-chartjs-2';
import StatCard from '../components/StatCard';
import MiniStatCard from '../components/MiniStatCard';
import CardSkeleton, { MiniCardSkeleton } from '../components/CardSkeleton';
import breakpoints from 'theme/breakpoints';
import colors from 'theme/colors';
import currency from 'helpers/currency';
import { Package, ShoppingCart, RotateCcw, Award } from 'lucide-react';
import { Badge } from '../components/RankingTable';
import dashboardStatistics from 'api/requests/dashboardStatistics';

export default function ProdutosTab({ periodParams }) {
    const [data, setData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!periodParams) return;

        setIsLoading(true);
        dashboardStatistics.getProdutos(periodParams)
            .then(response => setData(response.data))
            .catch(error => console.error('Erro ao carregar produtos:', error))
            .finally(() => setIsLoading(false));
    }, [periodParams]);

    if (isLoading) {
        return (
            <Container>
                <KpiGrid>
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                    <MiniCardSkeleton />
                </KpiGrid>
                <CardSkeleton height={250} />
                <ChartsGrid>
                    <CardSkeleton height={340} />
                    <CardSkeleton height={340} />
                </ChartsGrid>
            </Container>
        );
    }

    if (!data) return null;

    const { ticketMedio, ticketMedioTrend, totalVendidos, totalVendidosTrend, totalEstornos, totalEstornosTrend, produtoMaisPopular, produtoMaisVendido, vendasPorMes, topProdutos, porCategoria } = data;

    const vendasMesChartData = {
        labels: vendasPorMes.labels,
        datasets: [
            {
                label: 'Produtos vendidos',
                data: vendasPorMes.valores,
                backgroundColor: 'rgba(43, 200, 102, 0.7)',
                borderColor: '#2BC866',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
            {
                label: 'Estornos',
                data: vendasPorMes.estornos,
                backgroundColor: 'rgba(231, 80, 90, 0.7)',
                borderColor: '#E7505A',
                borderWidth: 1,
                borderRadius: 6,
                barPercentage: 0.6,
            },
        ],
    };

    const vendasMesOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    fontColor: '#999',
                    fontSize: 12,
                },
                gridLines: { color: '#f0f0f0', drawBorder: false },
            }],
            xAxes: [{
                ticks: { fontColor: '#999', fontSize: 12 },
                gridLines: { display: false },
            }],
        },
        legend: {
            position: 'top',
            align: 'end',
            labels: { boxWidth: 12, fontSize: 12 },
        },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            xPadding: 12,
            yPadding: 10,
        },
    };

    const categoriaChartData = {
        labels: porCategoria.map(c => c.nome),
        datasets: [{
            data: porCategoria.map(c => c.percentual),
            backgroundColor: porCategoria.map(c => c.cor),
            borderWidth: 0,
            hoverBorderWidth: 2,
            hoverBorderColor: '#fff',
        }],
    };

    const categoriaOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 65,
        legend: { display: false },
        tooltips: {
            backgroundColor: '#1A1A1A',
            cornerRadius: 8,
            callbacks: {
                label: function (tooltipItem, data) {
                    return ` ${data.labels[tooltipItem.index]}: ${data.datasets[0].data[tooltipItem.index]}%`;
                },
            },
        },
    };

    return (
        <Container>
            <KpiGrid>
                <MiniStatCard
                    label="Ticket Medio"
                    value={currency.format(ticketMedio)}
                    trend={ticketMedioTrend}
                    isPositive
                    icon={Package}
                />
                <MiniStatCard
                    label="Total Vendidos"
                    value={totalVendidos.toLocaleString('pt-BR')}
                    trend={totalVendidosTrend}
                    isPositive
                    icon={ShoppingCart}
                />
                <MiniStatCard
                    label="Total Estornos"
                    value={totalEstornos.toLocaleString('pt-BR')}
                    trend={totalEstornosTrend}
                    isPositive={false}
                    icon={RotateCcw}
                />
                <MiniStatCard
                    label="Produto Mais Popular"
                    value={produtoMaisPopular}
                    icon={Award}
                />
            </KpiGrid>

            <TopProdutosSection>
                <StatCard title="Top Produtos" subtitle="Produtos com melhor performance de vendas">
                    {topProdutos.length > 0 ? topProdutos.map((produto, index) => (
                        <ProdutoItem key={index}>
                            <ProdutoRank>#{index + 1}</ProdutoRank>
                            <ProdutoInfo>
                                <ProdutoNome>{produto.nome}</ProdutoNome>
                                <ProdutoSub>{produto.vendidos.toLocaleString('pt-BR')} vendidos</ProdutoSub>
                            </ProdutoInfo>
                            <ProdutoReceita>{currency.format(produto.receita)}</ProdutoReceita>
                        </ProdutoItem>
                    )) : (
                        <EmptyMessage>Nenhum produto vendido no periodo.</EmptyMessage>
                    )}
                </StatCard>
            </TopProdutosSection>

            <ChartsGrid>
                <StatCard title="Vendas de Produtos por Mes" subtitle="Vendas e estornos mensais">
                    <ChartContainer>
                        <Bar data={vendasMesChartData} options={vendasMesOptions} />
                    </ChartContainer>
                </StatCard>

                <StatCard title="Produtos por Categoria" subtitle="Distribuicao por tipo de produto">
                    {porCategoria.length > 0 ? (
                        <CategoriaContent>
                            <DoughnutWrap>
                                <Doughnut data={categoriaChartData} options={categoriaOptions} />
                            </DoughnutWrap>
                            <LegendList>
                                {porCategoria.map((item, index) => (
                                    <LegendItem key={index}>
                                        <LegendLeft>
                                            <LegendDot color={item.cor} />
                                            <LegendLabel>{item.nome}</LegendLabel>
                                        </LegendLeft>
                                        <LegendValue>{item.percentual}%</LegendValue>
                                    </LegendItem>
                                ))}
                            </LegendList>
                        </CategoriaContent>
                    ) : (
                        <EmptyMessage>Nenhuma categoria disponivel no periodo.</EmptyMessage>
                    )}
                </StatCard>
            </ChartsGrid>
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const KpiGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media screen and (max-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (max-width: ${breakpoints.sm}) {
        grid-template-columns: 1fr;
    }
`;

const TopProdutosSection = styled.div``;

const EmptyMessage = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0;
    padding: 12px 0;
`;

const ProdutoItem = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 14px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
`;

const ProdutoRank = styled.div`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3E8FF;
    color: ${colors.secondaryPurple};
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 13px;
    flex-shrink: 0;
`;

const ProdutoInfo = styled.div`
    flex: 1;
`;

const ProdutoNome = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const ProdutoSub = styled.div`
    font-size: 12px;
    color: ${colors.night};
    margin-top: 2px;
`;

const ProdutoReceita = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${colors.secondaryPurple};
`;

const ChartsGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;

    @media screen and (max-width: ${breakpoints.md}) {
        grid-template-columns: 1fr;
    }
`;

const ChartContainer = styled.div`
    height: 280px;
    width: 100%;
`;

const CategoriaContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 24px;
`;

const DoughnutWrap = styled.div`
    width: 180px;
    height: 180px;
`;

const LegendList = styled.div`
    width: 100%;
`;

const LegendItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
`;

const LegendLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const LegendDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ color }) => color};
`;

const LegendLabel = styled.span`
    font-size: 14px;
    color: ${colors.nightDark};
`;

const LegendValue = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;
